import { Download, Edit3, RefreshCw, Save, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { adminUpdateTimeLog, deleteTimeLog, getMasterData, getSettings, getTimeLogsPage } from "../../firebase/firestore.js";
import { getTodayKey } from "../../utils/date.js";
import { exportRowsToExcel } from "../../utils/export.js";
import { entryDifferenceLabel, exitDifferenceLabel, timingStatusLabel } from "../../utils/time.js";
import { logToExportRow } from "./adminUtils.js";

const initialFilters = {
  from: getTodayKey(),
  to: getTodayKey(),
  teacherId: "",
  classId: "",
  subjectId: "",
  roomId: "",
  classStatus: ""
};

export default function TimeLogManager() {
  const [master, setMaster] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { user } = useAuth();
  const { showToast } = useToast();

  async function load(reset = true) {
    setLoading(true);
    try {
      const [nextMaster, nextSettings, page] = await Promise.all([
        master ? Promise.resolve(master) : getMasterData(),
        settings ? Promise.resolve(settings) : getSettings(),
        getTimeLogsPage(filters, 50, reset ? null : cursor)
      ]);
      setMaster(nextMaster);
      setSettings(nextSettings);
      setRows((current) => (reset ? page.rows : [...current, ...page.rows]));
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (error) {
      showToast(error.message || "Unable to load time logs", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(true);
  }, []);

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await adminUpdateTimeLog(
        modal.log,
        {
          actualIn: modal.form.actualIn,
          actualOut: modal.form.actualOut,
          classStatus: modal.form.classStatus,
          suspensionReason: modal.form.suspensionReason
        },
        settings,
        user
      );
      showToast("Time log corrected");
      setModal(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to save time log", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteTimeLog(confirmDelete.id || confirmDelete.logId);
      showToast("Time log deleted");
      setConfirmDelete(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to delete time log", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Admin</p>
          <h2>Time Log Manager</h2>
        </div>
        <div className="toolbar-actions">
          <button className="btn btn-ghost" type="button" onClick={() => exportRowsToExcel(rows.map(logToExportRow), "pariksit-time-logs.xlsx", "Time Logs")}>
            <Download size={17} />
            Export
          </button>
          <button className="btn btn-primary" type="button" onClick={() => load(true)}>
            <RefreshCw size={17} />
            Apply
          </button>
        </div>
      </div>
      <div className="filters-row">
        <input type="date" value={filters.from} onChange={(event) => setFilters((value) => ({ ...value, from: event.target.value }))} />
        <input type="date" value={filters.to} onChange={(event) => setFilters((value) => ({ ...value, to: event.target.value }))} />
        <select value={filters.teacherId} onChange={(event) => setFilters((value) => ({ ...value, teacherId: event.target.value }))}>
          <option value="">All teachers</option>
          {(master?.teachers || []).map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}
        </select>
        <select value={filters.classId} onChange={(event) => setFilters((value) => ({ ...value, classId: event.target.value }))}>
          <option value="">All classes</option>
          {(master?.classes || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.subjectId} onChange={(event) => setFilters((value) => ({ ...value, subjectId: event.target.value }))}>
          <option value="">All subjects</option>
          {(master?.subjects || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.roomId} onChange={(event) => setFilters((value) => ({ ...value, roomId: event.target.value }))}>
          <option value="">All rooms</option>
          {(master?.rooms || []).map((item) => <option key={item.id} value={item.id}>{item.roomNumber}</option>)}
        </select>
        <select value={filters.classStatus} onChange={(event) => setFilters((value) => ({ ...value, classStatus: event.target.value }))}>
          <option value="">All statuses</option>
          <option value="normal">Normal</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {loading && !rows.length ? (
        <SkeletonList />
      ) : rows.length ? (
        <>
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Room</th>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Teacher</th>
                  <th>Scheduled</th>
                  <th>Actual</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Status</th>
                  <th>Updated By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((log) => (
                  <tr key={log.id}>
                    <td>{log.date}<br /><small>{log.day}</small></td>
                    <td>{log.roomNumber}</td>
                    <td>{log.className} - {log.sectionName}</td>
                    <td>{log.subjectName}</td>
                    <td>{log.teacherName}</td>
                    <td>{log.scheduledIn} - {log.scheduledOut}</td>
                    <td>{log.actualIn || "-"} - {log.actualOut || "-"}</td>
                    <td><StatusChip status={timingStatusLabel(log, "entry")} /><br /><small>{entryDifferenceLabel(log)}</small></td>
                    <td><StatusChip status={timingStatusLabel(log, "exit")} /><br /><small>{exitDifferenceLabel(log)}</small></td>
                    <td><StatusChip status={log.classStatus === "suspended" ? "Suspended" : "Normal"} /></td>
                    <td>{log.updatedByName || log.updatedBy || "-"}</td>
                    <td className="table-actions compact-actions">
                      <button
                        className="icon-button"
                        type="button"
                        onClick={() =>
                          setModal({
                            log,
                            form: {
                              actualIn: log.actualIn || "",
                              actualOut: log.actualOut || "",
                              classStatus: log.classStatus || "normal",
                              suspensionReason: log.suspensionReason || ""
                            }
                          })
                        }
                      >
                        <Edit3 size={17} />
                      </button>
                      <button className="icon-button danger" type="button" onClick={() => setConfirmDelete(log)}>
                        <Trash2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <button className="btn btn-ghost btn-wide" type="button" onClick={() => load(false)} disabled={loading}>
              {loading ? <span className="spinner" /> : null}
              Load more
            </button>
          ) : null}
        </>
      ) : (
        <EmptyState title="No logs found" text="Try a different date range or filter." />
      )}

      <Modal open={Boolean(modal)} title="Correct Time Log" onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <div className="selected-routine">
            <strong>{modal?.log.teacherName}</strong>
            <span>{modal?.log.date} · Room {modal?.log.roomNumber} · {modal?.log.subjectName}</span>
          </div>
          <Field label="Actual IN"><input type="time" value={modal?.form.actualIn || ""} onChange={(event) => setModal((value) => ({ ...value, form: { ...value.form, actualIn: event.target.value } }))} /></Field>
          <Field label="Actual OUT"><input type="time" value={modal?.form.actualOut || ""} onChange={(event) => setModal((value) => ({ ...value, form: { ...value.form, actualOut: event.target.value } }))} /></Field>
          <Field label="Class status">
            <select value={modal?.form.classStatus || "normal"} onChange={(event) => setModal((value) => ({ ...value, form: { ...value.form, classStatus: event.target.value } }))}>
              <option value="normal">Normal</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
          <Field label="Suspension reason">
            <textarea value={modal?.form.suspensionReason || ""} onChange={(event) => setModal((value) => ({ ...value, form: { ...value.form, suspensionReason: event.target.value } }))} />
          </Field>
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}><X size={17} />Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : <Save size={17} />}Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete time log?"
        message={`Delete log for ${confirmDelete?.teacherName || "this class"} on ${confirmDelete?.date || ""}?`}
        confirmLabel="Delete"
        loading={saving}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
